import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {from, ReplaySubject, Subject, Subscription} from "rxjs";
import { filter, finalize,  take, takeUntil, tap} from "rxjs/operators";
import {MatSelectChange} from "@angular/material/select";
import {DynamicDictionary} from "../../interface/dynamic-dictionary";
import {DynamicDictService} from "../../services/dynamic-dict.service";
import {Dictionary} from "../../interface/dictionary";

@Component({
  selector: 'app-dynamic-dict',
  templateUrl: './dynamic-dict.component.html',
  styleUrls: ['./dynamic-dict.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DynamicDictComponent)}
  ]
})
export class DynamicDictComponent implements OnInit, ControlValueAccessor, OnDestroy, OnChanges {
  @Input() dictType: string;
  @Input() dictTypeSubject: Subject<string>;
  @Input() label: string;
  @Input() multiple = false;
  @Input() control?: FormControl = null;
  @Input() required = false;
  @Input() disable = false;
  @Input() returnObject = false;
  @Input() allowCustomValue = false;
  @Output() selectedDict: EventEmitter<DynamicDictionary> = new EventEmitter<DynamicDictionary>();
  @Output() selectedMultipleDict: EventEmitter<DynamicDictionary[]> = new EventEmitter<DynamicDictionary[]>();
  @Output() availableItems: EventEmitter<DynamicDictionary[]> = new EventEmitter<DynamicDictionary[]>();


  externalControl = true;
  disabled = false;
  items: DynamicDictionary[] = [];
  filteredItems: DynamicDictionary[] = null;
  value?: any = null;
  currentColor = '';
  filterControl: FormControl;
  dictTypeSubjectSub?: Subscription = null;
  showCustomValue = false;
  customValueControl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private pendingValue: any = null;
  constructor(
    private dunamicDictService: DynamicDictService,
    private fb: FormBuilder,
  ) {
    this.filterControl = fb.control('');
  }

  compareFn(item1: any, item2: any): boolean {
    if (item1 == null || item2 == null) {
      return item1 === item2;
    }

    if (typeof item1 === 'object' && typeof item2 === 'object' && item1.id && item2.id) {
      return item1.id === item2.id;
    }

    if (typeof item1 === 'object' && typeof item2 === 'string') {
      return item1.name === item2;
    }
    if (typeof item1 === 'string' && typeof item2 === 'object') {
      return item1 === item2.name;
    }

    return item1 === item2;
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    if (this.allowCustomValue) {
      this.customValueControl = new FormControl();
      if (this.disable) {
        this.customValueControl.disable();
      } else {
        this.customValueControl.enable();
      }
    }

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val?: string) => {
        if (!val) {
          this.filteredItems = null;
          return;
        }

        this.filteredItems = this.items;

        this.filteredItems = this.filteredItems.filter((dynamicDictionary: DynamicDictionary) => {
          return dynamicDictionary.name.toUpperCase().includes(val.toUpperCase());
        });
      });

    this.customValueControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.selectedDict.emit(val);
        this.onChange(val);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dictTypeSubject) {
      if (this.dictTypeSubjectSub === null) {
        this.dictTypeSubjectSub = this.dictTypeSubject
          .pipe(takeUntil(this.destroyed$))
          .subscribe((dictType: string) => this.loadItems(dictType));
      }
    } else {
      this.loadItems(this.dictType);
    }

    if (changes.disable) {
      if (this.disable) {
        this.customValueControl.disable();
      } else {
        this.customValueControl.enable();
      }
    }
  }

  loadItems(dictType: string): void {
    this.items = [];
    this.dunamicDictService.getDynamicDictSubject()
      .pipe(
        take(1)
      )
      .subscribe((items: DynamicDictionary[]) => {
        from(items)
          .pipe(
            filter((e: DynamicDictionary) => e.type.key === dictType),
            tap((e: DynamicDictionary) => {
              if (!this.items.some((search: DynamicDictionary) => search.id === e.id)) {
                this.items.push(e);
              }
            }),
            finalize(() => {
              this.items = this.items.sort((a: DynamicDictionary, b: DynamicDictionary) => {
                return a.name.localeCompare(b.name);
              });

              if (this.allowCustomValue) {
                this.items.push({
                  id: 0,
                  name: 'Inne',
                  type: null,
                  deleted: false,
                });
              }

              this.availableItems.emit(this.items);

              if (this.pendingValue !== null) {
                this.resolvePendingValue();
              }
            })
          ).subscribe();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    if ((this.returnObject || this.allowCustomValue) && typeof value === 'string' && value.trim() !== '') {
      this.pendingValue = value;

      if (this.items.length > 0) {
        this.resolvePendingValue();
      } else {
        this.control.setValue(value);
        this.value = value;
      }
    } else {
      this.control.setValue(value);
      this.value = value;
    }
  }

  resolvePendingValue(): void {
    if (!this.pendingValue) {return; }

    if (this.multiple && Array.isArray(this.pendingValue)) {
    } else {
      const foundItem = this.items.find(item => item.name === this.pendingValue);

      if (foundItem) {
        this.control.setValue(foundItem);
        this.value = foundItem;
      }

      if (this.allowCustomValue && !foundItem) {
        this.control.setValue('Inne');
        this.customValueControl.setValue(this.pendingValue);
        this.showCustomValue = true;
      }
    }

    this.pendingValue = null;
  }

  onSelectionChange(e: MatSelectChange): void {
    if (this.multiple) {
      const selected = [];
      this.items.forEach((dict: DynamicDictionary) => {
        const isSelected = this.returnObject
          ? e.value?.some((val: any) => val?.id === dict.id)
          : e.value?.includes(dict.id);

        if (isSelected) {
          selected.push(dict);
        }

        this.selectedMultipleDict.emit(selected);
      });
    } else {
      const selectedDict = (this.returnObject || this.allowCustomValue)
        ? e.value
        : this.items.find((dict: DynamicDictionary) => dict.id === e.value);

      if (selectedDict) {
        this.selectedDict.emit(selectedDict);
      }

      if (this.allowCustomValue && selectedDict === 'Inne'){
        this.showCustomValue = true;
        this.customValueControl.enable();
        this.value = null;
        this.selectedDict.emit(null);
      } else {
        this.showCustomValue = false;
        this.customValueControl.disable();
        this.customValueControl.reset();
      }
    }

    if (!this.showCustomValue){
      this.value = e.value;
    }
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
    if (this.allowCustomValue && this.showCustomValue){
      this.showCustomValue = false;
      this.customValueControl.reset();
      this.customValueControl.disable();
    }
  }
}
